/**
 * DataEndpoint - endpoint for data types (number, boolean, string, array, pattern)
 *
 * Wraps store access and handles value propagation through connections.
 *
 * Getter resolution:
 *   get = incomingGet ?? configGet ?? null
 *   - incomingGet: bound getter from upstream connection factory
 *   - configGet: bound getter from own useExp definition config
 *   Connection wins. On disconnect, falls back to configGet.
 *
 * Factory propagation:
 *   Getters propagate as factories (ctx => dt => result).
 *   Each target binds the factory once with a live context object —
 *   value/min/max/step are getter properties that read fresh each call.
 *   No per-frame re-binding needed.
 */

import type { IEndpoint } from '@/runtime/IEndpoint';
import { PropagatingEndpoint } from '@/runtime/PropagatingEndpoint';
import type { IRuntimeStore } from '@runtime/RuntimeStore';
import { scaleValue, clampToRange, resolveConfigValue, isWrappedConfig } from '@runtime/util';
import { Endpoint, DataTypeSpec, DataFlow } from '@/nodes/Endpoint';
import type { Payload } from '@/graph/Payload';
import { EndpointWithContext } from '../Plugin';
import { logger } from '@/logging';

const MISSING_PARAM = Object.freeze({ value: undefined, min: undefined, max: undefined, step: undefined, dataType: undefined });

/**
 * Hash a string to a 32-bit integer seed.
 */
function hashString(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return h;
}

/**
 * Create a seeded `rnd` function for expression context.
 * - rnd(min, max)       → random number in [min, max]
 * - rnd([a, b, c, ...]) → random pick from array
 * Deterministic per seed — same results every frame.
 */
export function makeRnd(seed: number): (...args: any[]) => number {
  let s = seed | 0;
  const next = () => {
    s = (s * 1664525 + 1013904223) | 0;
    return (s >>> 0) / 0xffffffff;
  };
  return (...args: any[]) => {
    if (args.length === 1 && Array.isArray(args[0])) {
      const arr = args[0];
      return arr[Math.floor(next() * arr.length)];
    }
    const min = args[0] ?? 0;
    const max = args[1] ?? 1;
    return min + next() * (max - min);
  };
}

export interface GetterFactory {
  (ctx: Record<string, any>): (dt: number) => any;
  /** Original expression source text — stored in payload instead of stringified function */
  _source?: string;
}

/** Build a host object with live getter properties for sibling endpoints.
 *  Accessing a non-existent param returns a safe fallback and logs an error. */
export function buildHost(endpoints: Record<string, IEndpoint> | undefined, store: IRuntimeStore): Record<string, any> {
  const host: Record<string, any> = {};
  if (!endpoints) return new Proxy(host, {
    get(_t, prop) { if (typeof prop === 'string') { logger.error('host', `param '${prop}' not found`); } return MISSING_PARAM; }
  });
  for (const [id, ep] of Object.entries(endpoints)) {
    const storeKey = (ep as any).storeKey;
    host[id] = {
      get value() { return resolveConfigValue(store.getPayload(storeKey)); },
      get min() { return (ep as any).min; },
      get max() { return (ep as any).max; },
      get step() { return (ep as any).step; },
      dataType: (ep as any).dataType,
    };
  }
  return new Proxy(host, {
    get(target, prop, receiver) {
      if (typeof prop === 'string' && !(prop in target)) {
        logger.error('host', `param '${prop}' not found on node`);
        return MISSING_PARAM;
      }
      return Reflect.get(target, prop, receiver);
    }
  });
}

export class DataEndpoint extends PropagatingEndpoint implements Endpoint {
  // Identity
  readonly id: string;
  readonly dataType: DataTypeSpec;
  readonly dataFlow: DataFlow;

  // Data-specific metadata
  readonly scaleIncomingValues?: boolean;
  readonly inheritMetadata?: boolean;
  readonly isPresetParam?: boolean;

  // ── Getter resolution ──
  /** Factory from own useExp definition config */
  configGetFactory: GetterFactory | null = null;
  /** Factory from upstream connection */
  incomingGetFactory: GetterFactory | null = null;

  /** Bound getter from own useExp */
  private _configGet: ((dt: number) => any) | null = null;
  /** Bound getter from upstream connection */
  private _incomingGet: ((dt: number) => any) | null = null;

  /** Active getter: incoming connection wins over own configGet */
  get get(): ((dt: number) => any) | null {
    return this._incomingGet ?? this._configGet ?? null;
  }
  /** Dummy setter — prevents TypeError from Object.assign in super(). Use configGetFactory/incomingGetFactory instead. */
  set get(_: any) { /* no-op */ }

  /** Sibling endpoints on the same node, set during initialization for use in bindGetter */
  siblingEndpoints?: Record<string, IEndpoint>;

  constructor(config: EndpointWithContext, store: IRuntimeStore) {
    super(config, store);
    this.id = config.endpoint.id;
    this.dataType = config.endpoint.dataType;
    this.dataFlow = config.endpoint.dataFlow;
    this.scaleIncomingValues = config.endpoint.scaleIncomingValues;
    this.inheritMetadata = config.endpoint.inheritMetadata;
    this.isPresetParam = config.endpoint.isPresetParam;
  }

  /** Bind configGetFactory with current context to produce _configGet */
  bindGetter(ppq = 48, beatsPerBar = 4, siblings?: Record<string, IEndpoint>, getTime?: () => number): void {
    if (siblings) this.siblingEndpoints = siblings;
    if (getTime) this._getTime = getTime;
    if (!this.configGetFactory) { this._configGet = null; return; }
    this._configGet = this.bindFactory(this.configGetFactory, ppq, beatsPerBar);
  }

  /** Transport time getter, set during bindGetter */
  private _getTime: () => number = () => 0;

  /** Bind a factory with this endpoint's context.
   *  value reads live from store each getter call. min/max/step read live from endpoint.
   *  host uses live getter properties for sibling endpoints. */
  private bindFactory(factory: GetterFactory, ppq = 48, beatsPerBar = 4): (dt: number) => any {
    const ep = this;
    const seed = hashString(`${ep.storeKey.instancePath}:${ep.storeKey.nodeId}:${ep.storeKey.endpointId}`);
    const ctx = {
      get value() { return resolveConfigValue(ep.store.getPayload(ep.storeKey)); },
      get min() { return ep.min; },
      get max() { return ep.max; },
      get step() { return ep.step; },
      /** Fresh seeded rnd each access — deterministic per frame */
      get rnd() { return makeRnd(seed); },
      /** Live transport time — read each getter call */
      get _time() { return ep._getTime(); },
      _ppq: ppq,
      _bpb: beatsPerBar,
      host: buildHost(this.siblingEndpoints, this.store),
    };
    return factory(ctx);
  }

  // ─────────────────────────────────────────────────────────────
  // Value Access (through store or get function)
  // ─────────────────────────────────────────────────────────────

  getValue(dt?: number): Payload {
    if (this.get) {
      try {
        const factory = (this.incomingGetFactory ?? this.configGetFactory)!;
        return { value: this.get(dt), getFactory: factory };
      } catch (e) {
        logger.error('DataEndpoint', `expression error on ${this.id}: ${e}`);
        return this.store.getPayload(this.storeKey);
      }
    }
    return this.store.getPayload(this.storeKey);
  }


  // ─────────────────────────────────────────────────────────────
  // TARGET/INPUT BEHAVIOUR
  // ─────────────────────────────────────────────────────────────

  /**
 * Set value, notify node (unless source is self), and propagate to targets.
 * @param source - If provided, skip this endpoint when propagating. If source === this, skip notifying node (node is emitting).
 */
  setValue(payload: Payload, source?: IEndpoint): void {
    // Strip getFactory before storing (internal propagation only)
    let storePayload = payload;
    if (storePayload && typeof storePayload === 'object' && 'getFactory' in storePayload) {
      const { getFactory: _, ...rest } = storePayload;
      storePayload = rest;
    }

    // If endpoint has an incoming getter, store expression source for UI detection
    const incomingSource = this.incomingGetFactory?._source;
    if (this._incomingGet && incomingSource) {
      const wrapped = (storePayload && typeof storePayload === 'object' && 'value' in storePayload)
        ? storePayload : { value: storePayload };
      storePayload = { ...wrapped, get: incomingSource };
    }

    // Store wraps value as payload and returns it
    const stored = this.store.setValue(this.storeKey, storePayload);

    // :out marker indicates value was set
    this.store.setValue(this.storeKeyOut, stored);

    // Params and triggers: notify even without source (config values / UI button clicks)
    // Others: only notify if source exists and isn't self
    const shouldNotify = (this.dataFlow === 'param' || this.dataFlow === 'trigger')
      ? (source !== this)
      : (source && source !== this);

    if (shouldNotify) {
      this.notifyNode(stored, source);
    }

    // Propagate with factory so targets can re-bind with their own context
    const factory = this.configGetFactory ?? this.incomingGetFactory;
    const propagatePayload = factory
      ? { ...stored, getFactory: factory }
      : stored;

    this.propagate(propagatePayload, source);
  }

  /**
   * Receive a value from an upstream graph connection.
   * Factory payloads are re-bound with local context. Normal values are stored and propagated.
   */
  receive(payload: Payload, source: IEndpoint): void {
    const hasFactory = payload && typeof payload === 'object' && 'getFactory' in payload && typeof payload.getFactory === 'function';

    // ── Factory arrival: bind with local context, set as incomingGet ──
    if (hasFactory) {
      // Factories from our own internal endpoint (output bubbling up from inside a subgraph)
      // should pass through to downstream graph connections but NOT bind as adopted getter.
      // The exposed endpoint's own configGetFactory (useExp) controls its expression state.
      if (source === this.internalEndpoint) {
        const current = this.store.getPayload(this.storeKey) ?? { value: undefined };
        // Store expression source for UI detection on the exposed endpoint
        const expSource = (payload.getFactory as GetterFactory)?._source;
        if (expSource) {
          this.store.setValue(this.storeKey, { ...current, get: expSource });
        }
        this.propagate({ ...current, getFactory: payload.getFactory as GetterFactory }, source);
        return;
      }

      this.incomingGetFactory = payload.getFactory as GetterFactory;
      this._incomingGet = this.bindFactory(this.incomingGetFactory);
      // Store expression source for UI detection, preserve value
      const current = this.store.getPayload(this.storeKey) ?? { value: undefined };
      const expSource = this.incomingGetFactory?._source;
      if (expSource) {
        this.store.setValue(this.storeKey, { ...current, get: expSource });
      }
      // Re-propagate factory to downstream targets
      this.propagate({ ...current, getFactory: this.incomingGetFactory }, source);
      return;
    }

    // ── Getter cleared: restore configGet, clean store ──
    const hadIncoming = this._incomingGet !== null;
    this._incomingGet = null;
    this.incomingGetFactory = null;

    if (hadIncoming) {
      // Remove stale get from store, preserve value
      const current = this.store.getPayload(this.storeKey);
      if (current && typeof current === 'object' && 'get' in current) {
        const { get: _, ...rest } = current;
        this.store.setValue(this.storeKey, rest);
      }
      // Propagate current state so downstream clears their incoming getters
      const updated = this.store.getPayload(this.storeKey);
      if (updated) {
        const factory = this.configGetFactory;
        const propagatePayload = factory
          ? { ...updated, getFactory: factory }
          : updated;
        this.propagate(propagatePayload, source);
      }
      return;
    }

    // ── Normal value (no getter transition) — store, notify, propagate ──
    const processed = this.scaleIncoming(payload, source);
    this.store.setValue(this.storeKeyIn, processed);
    this.setValue(processed, source);
  }

  /** Restore configGet when upstream disconnects, clean store, propagate */
  onSourceDisconnected(_source: IEndpoint): void {
    this._incomingGet = null;
    this.incomingGetFactory = null;
    // configGet is untouched — falls back automatically via readonly get
    const current = this.store.getPayload(this.storeKey);
    if (current) {
      let clean = current;
      if (clean && typeof clean === 'object' && 'get' in clean) {
        const { get: _, ...rest } = clean;
        clean = rest;
        this.store.setValue(this.storeKey, clean);
      }
      const factory = this.configGetFactory;
      const propagatePayload = factory
        ? { ...clean, getFactory: factory }
        : clean;
      this.propagate(propagatePayload);
    }
  }

  /**
   * Scale/clamp incoming value based on source and target metadata.
   * Handles wrapped payloads ({value: X}) - unwraps, processes, rewraps.
   */
  private scaleIncoming(payload: any, source: IEndpoint): any {
    // Handle wrapped payloads
    const wrapped = isWrappedConfig(payload);
    const value = resolveConfigValue(payload);

    // Only process numbers
    if (typeof value !== 'number') return payload;

    let scaled = value;

    // Scale if requested and source has range metadata (DataEndpoints only)
    const sourceMin = (source as any).min;
    const sourceMax = (source as any).max;
    if (this.scaleIncomingValues) {
      if (
        sourceMin !== undefined &&
        sourceMax !== undefined &&
        this.min !== undefined &&
        this.max !== undefined &&
        (sourceMin !== this.min || sourceMax !== this.max)
      ) {
        scaled = scaleValue(value, { min: sourceMin, max: sourceMax }, { min: this.min, max: this.max });
      }
    }

    // Clamp to target range only when scaling is enabled
    if (this.scaleIncomingValues && this.min !== undefined && this.max !== undefined) {
      scaled = clampToRange(scaled, this.min, this.max);
    }

    // Rewrap if original was wrapped
    return wrapped ? { ...(payload as object), value: scaled } : scaled;
  }
}
